/**
Project: Voice Panel (c)
Title: Signin Silent
Description: Component for displaying the teams sign in page 
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import axios from "axios";
import { Button, Flex, Text, Image, Form } from "@fluentui/react-northstar";
import * as microsoftTeams from "@microsoft/teams-js";
import { AuthenticationContext } from "react-adal";
import IntlTranslate from "../helpers/intl-translate";
import IntlUtil from "../helpers/intl-util";
import SippioVoicePanelLogo from "../../../assets/images/SIPPIO-VoicePanel-Logo.png";
import TokenUtil from "../helpers/token-util";
import { authenticateTokenSignin } from "../actions/identity-actions";
import { AppURLProps } from "../settings/app-urls";
import { AppConfigProps } from "../settings/app-config";
import ResponseUtil from "../helpers/response-util";
import { AppConstants } from "../settings/app-constants";
import {
  accessLogWithToken,
  openConsentPopup,
} from "../actions/common-actions";
import PageUtil from "../helpers/page-util";

class SigninSilent extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _authContext = null;
  _userObjectId = null;
  _config = null;
  _intl_ns_common = "common";
  _intl_ns_signin = "signin";

  constructor(props) {
    super(props);
    this.signinForm = React.createRef();
    this.loginIdField = React.createRef();
    this.passwordField = React.createRef();

    this.state = {
      isFormDataSubmitted: false,
      isFormDataFilled: false,
      formInputFields: this.initFormInputFields(),
      isSigninFail: false,
      errorMessage: null,
      errorDetails: null,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    await this.getAuthenticationContext();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  initFormInputFields = () => {
    return {
      loginId: {
        fieldRef: this.loginIdField,
        fieldValue: "",
      },
      password: {
        fieldRef: this.passwordField,
        fieldValue: "",
      },
    };
  };

  getAuthenticationContext = async () => {
    microsoftTeams.initialize();
    localStorage.clear();
    const azureActiveDirectoryAppId =
      process.env.REACT_APP_AZURE_ACTIVE_DIRECTORY_APP_ID;
    microsoftTeams.getContext((ctx) => {
      let loginHint = ctx.loginHint;
      let userObjectId = ctx.userObjectId;

      let tenantId = ctx.tid;

      let config = {
        tenant: tenantId,
        clientId: azureActiveDirectoryAppId,
        redirectUri: window.location.origin + AppURLProps.signinSilentEnd, // This should be in the list of redirect uris for the AAD app
        cacheLocation: "localStorage",
        navigateToLoginRequestUrl: false,
        extraQueryParameter:
          "scope=openid+profile&login_hint=" + encodeURIComponent(loginHint),
      };

      this._authContext = new AuthenticationContext(config);
      this._config = config;
      this._userObjectId = userObjectId;
    });
  };

  checkFormData = async () => {
    if (this.state.formInputFields) {
      if (
        this.state.formInputFields.loginId.fieldValue !== "" &&
        this.state.formInputFields.password.fieldValue !== ""
      ) {
        await this.setStateAsync({ isFormDataFilled: true });
      } else {
        await this.setStateAsync({ isFormDataFilled: false });
      }
    }
  };

  handleFormFieldChange = async (e) => {
    await this.setStateAsync({
      formInputFields: {
        ...this.state.formInputFields,
        [e.target.name]: {
          ...this.state.formInputFields[e.target.id],
          fieldValue: e.target.value,
        },
      },
    });
    await this.checkFormData();
  };

  clearSigninError = async () => {
    await this.setStateAsync({ isFormDataSubmitted: true });
    await this.setStateAsync({ isSigninFail: false });
    await this.setStateAsync({ errorMessage: null });
    await this.setStateAsync({ errorDetails: null });
  };

  initiateTeamsSignin = async (e) => {
    this.clearSigninError();
    e.preventDefault();

    let that = this;
    const azureActiveDirectoryAppId =
      process.env.REACT_APP_AZURE_ACTIVE_DIRECTORY_APP_ID;

    let errorMessage = IntlUtil.getText(
      this._intl_ns_signin,
      "notification.error.signinCancel"
    );

    microsoftTeams.authentication.authenticate({
      url: window.location.origin + AppURLProps.signinSilentStart,
      width: 600,
      height: 535,
      successCallback: async function (result) {
        let authContext = that._authContext;
        let token = authContext.getCachedToken(azureActiveDirectoryAppId);
        if (token) {
          that.handleTeamsSignin(token, that.props.history);
        } else {
          that.signinRedirect();
        }
      },
      failureCallback: function (reason) {
        if (
          reason &&
          (reason === "CancelledByUser" || reason === "FailedToOpenWindow")
        ) {
          that.setStateAsync({
            errorMessage: errorMessage,
          });
          that.setStateAsync({ isSigninFail: true });
          that.setStateAsync({ isFormDataSubmitted: false });
        } else {
          that.signinRedirect();
        }
      },
    });
  };

  handleTeamsSignin = async (token, history) => {
    this.clearSigninError();
    try {
      let res = await authenticateTokenSignin(token);
      if (
        res &&
        res.status === AppConfigProps.httpStatusCode.ok &&
        res.data &&
        res.data.result &&
        res.data.result.accessToken &&
        res.data.result.refreshToken
      ) {
        await TokenUtil.setIdentityToken(
          res.data.result.accessToken,
          res.data.result.refreshToken
        );
        const tokenData = TokenUtil.getIdentitySippioAccessTokenData();
        if (
          tokenData.accountNumber === null ||
          tokenData.accountNumber === ""
        ) {
          let errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.noAccountId"
          );
          this.setSigninError(errorMessage, null);
          TokenUtil.removeIdentityToken();
        } else if (
          tokenData.regionsTemplate === null ||
          tokenData.regionsTemplate === ""
        ) {
          let errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.noRegions"
          );
          this.setSigninError(errorMessage, null);
          TokenUtil.removeIdentityToken();
        } else {
          accessLogWithToken(
            PageUtil.getPageURL(this.props.history.location),
            "UID-SIS",
            "Sign-in Success",
            res.data.result.accessToken
          );
          history.push(AppURLProps.home);
        }
      } else {
        accessLogWithToken(
          PageUtil.getPageURL(this.props.history.location),
          "UID-SIF",
          "Sign-in Failure",
          token
        );
        let errorMessage = IntlUtil.getText(
          this._intl_ns_signin,
          "notification.error.signinIssueUnknown"
        );
        await this.setSigninError(errorMessage, null);
      }
    } catch (err) {
      accessLogWithToken(
        PageUtil.getPageURL(this.props.history.location),
        "UID-SIF",
        "Sign-in Failure",
        token
      );
      let errorMessage = IntlUtil.getText(
        this._intl_ns_signin,
        "notification.error.signinIssueUnknown"
      );
      let errorDetails = [];
      if (err && err.status) {
        if (err.status === AppConfigProps.httpStatusCode.badRequest) {
          errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.signinIssueUnknown"
          );
        } else if (err.status === AppConfigProps.httpStatusCode.forbidden) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.ADMIN_PRIVILEGES
          ) {
            let authContext =
              AuthenticationContext.prototype._singletonInstance;
            errorMessage =
              authContext._user.userName +
              ", " +
              IntlUtil.getText(
                this._intl_ns_signin,
                "notification.error.privilegeError"
              );
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.ADMIN_CONSENT
          ) {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinNoConsent"
            );
            if (primeError.message && primeError.message.trim().length > 0) {
              errorDetails.push(primeError.message);
            }
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.LYNC_RESOURCE
          ) {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinNoLync"
            );
            if (primeError.message && primeError.message.trim().length > 0) {
              errorDetails.push(primeError.message);
            }
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.SIGNIN_DIRECTORY
          ) {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinNoDirectory"
            );
            if (primeError.message && primeError.message.trim().length > 0) {
              errorDetails.push(primeError.message);
            }
          } else {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinError"
            );
          }
        }
      }
      // localStorage.clear();
      await this.setSigninError(errorMessage, errorDetails);
    }
  };

  setSigninError = async (errorMessage, errorDetails) => {
    await this.setStateAsync({ isFormDataSubmitted: false });
    await this.setStateAsync({ isSigninFail: true });
    await this.setStateAsync({ errorMessage: errorMessage });
    if (errorDetails && errorDetails.length > 0) {
      await this.setStateAsync({ errorDetails: errorDetails });
    }
  };

  signinRedirect = async () => {
    let that = this;
    const { history } = this.props;
    let errorMessage = IntlUtil.getText(
      this._intl_ns_signin,
      "notification.error.signinFail"
    );
    microsoftTeams.getContext((ctx) => {
      let authContext = this._authContext;
      let user = authContext.getCachedUser();
      if (user) {
        if (user.profile.oid !== this._userObjectId) {
          // User doesn't match, clear the cache
          authContext.clearCache();
        }
      }
      authContext.acquireToken(
        this._config.clientId,
        function (errDesc, token, err, tokenType) {
          let errorDetails = [];
          if (token) {
            // Make sure ADAL gave us an id token
            if (tokenType !== authContext.CONSTANTS.ID_TOKEN) {
              token = authContext.getCachedToken(that._config.clientId);
            }
            that.handleTeamsSignin(token, history);
          } else {
            if (err) {
              errorDetails.push(
                IntlUtil.getText(that._intl_ns_signin, "content.error") + err
              );
            }
            if (errDesc) {
              errorDetails.push(
                IntlUtil.getText(
                  that._intl_ns_signin,
                  "content.errorDescription"
                ) + errDesc
              );
            }
            that.setStateAsync({
              errorMessage: errorMessage,
            });
            if (errorDetails && errorDetails.length > 0) {
              that.setStateAsync({ errorDetails: errorDetails });
            }
            that.setStateAsync({ isSigninFail: true });
            that.setStateAsync({ isFormDataSubmitted: false });
          }
        }
      );
    });
  };

  consentLinkClick = async () => {
    await openConsentPopup();
  };

  render() {
    return (
      <>
        <Flex
          gap="gap.small"
          hAlign="center"
          vAlign="center"
          className="signin-page-wrapper"
          column
        >
          <Flex column gap="gap.large" className="signin-silent-wrapper">
            <Flex column gap="gap.medium" hAlign="center">
              <Image
                src={SippioVoicePanelLogo}
                alt={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.voicePanelBySippio"
                )}
                title={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.voicePanelBySippio"
                )}
              />
            </Flex>
            <Flex column gap="gap.small" hAlign="start">
              <Flex column hAlign="start">
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo1"
                  )}
                  className="signin-text"
                />
              </Flex>
              <Flex column hAlign="start">
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo2"
                  )}
                  className="signin-text"
                />
              </Flex>
              <Flex column hAlign="start">
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo3"
                  )}
                  className="signin-text"
                />
              </Flex>
              <div>
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.note"
                  )}
                  className="signin-text text-bold"
                />
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo4"
                  )}
                  className="signin-text"
                />
                <span className="text-link" onClick={this.consentLinkClick}>
                  {IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.acceptConsent"
                  )}
                </span>
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo5"
                  )}
                  className="signin-text"
                />
              </div>
            </Flex>
            <Flex column gap="gap.medium" hAlign="center">
              <Form noValidate onSubmit={() => {}}>
                {this.state.errorMessage ? (
                  <>
                    <Text
                      content={this.state.errorMessage}
                      error={true}
                      className="signin-text"
                    />
                    {this.state.errorDetails &&
                    this.state.errorDetails.length > 0 ? (
                      <Flex column gap="gap.small" className="signin-div">
                        {this.state.errorDetails.map((error) => {
                          return <Text content={error} />;
                        })}
                      </Flex>
                    ) : null}
                  </>
                ) : null}
                <Flex column gap="gap.medium" hAlign="center">
                  {this.state.isFormDataSubmitted ? (
                    <Button
                      type="submit"
                      primary
                      className="signin-button-animate"
                    >
                      <span className="signin-button-text">
                        <IntlTranslate
                          namespace={this._intl_ns_signin}
                          keyId="content.signin"
                        />
                      </span>
                      <span className="signin-button-fadebar" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      content={
                        <IntlTranslate
                          namespace={this._intl_ns_signin}
                          keyId="content.signin"
                        />
                      }
                      primary
                      className="signin-button"
                      onClick={this.initiateTeamsSignin}
                    />
                  )}
                </Flex>
              </Form>
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  }
}

export default SigninSilent;
